import React, { useState } from "react";
import Seo from "../components/Seo";
import { Link } from "react-router-dom";
import { ArrowRight, Check, Plus } from "lucide-react";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "../components/ui/dialog";
import PageHero from "../components/PageHero";
import { Reveal } from "../lib/motion";
import { useLang } from "../i18n/LanguageContext";
import { IMAGES, SERVICES_FULL } from "../data";

export default function Services() {
  const { t } = useLang();
  const [active, setActive] = useState(null);

  return (
    <div data-testid="services-page" className="bg-ink-950">
      <Seo title="Services" path="/services" description="Professional cleaning and facility services in Montreal — office, commercial, residential, move-out and post-construction cleaning across the Greater Montreal Area." keywords="Commercial Cleaning Montreal, Office Cleaning Montreal, Facility Services Montreal" />
      <PageHero
        overline={t("Services")}
        title={t("Every service your facility needs, delivered with precision.")}
        subtitle={t("Ten specialized programs, one accountable partner — engineered for the standards of premium commercial properties.")}
        image={IMAGES.windowClean}
      />

      <section className="py-24 md:py-32">
        <div className="max-w-7xl mx-auto px-6 md:px-12 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {SERVICES_FULL.map((s, i) => (
            <Reveal key={s.title} delay={(i % 3) * 0.08}>
              <div data-testid={`service-card-${i}`} className="group glass rounded-3xl overflow-hidden card-hover h-full flex flex-col">
                <div className="relative aspect-[16/10] overflow-hidden">
                  <img src={IMAGES[s.img]} alt={s.title} className="absolute inset-0 w-full h-full object-cover opacity-55 group-hover:opacity-75 group-hover:scale-110 transition-all duration-700 ease-out" />
                  <div className="absolute inset-0 bg-gradient-to-t from-ink-900 via-ink-900/30 to-transparent" />
                  <div className="absolute top-4 left-4 h-11 w-11 rounded-2xl glass flex items-center justify-center">
                    <s.icon className="h-5 w-5 text-steel-light" strokeWidth={1.5} />
                  </div>
                </div>
                <div className="p-7 flex flex-col flex-1">
                  <h3 className="font-serif text-2xl text-white" style={{ fontWeight: 500 }}>{t(s.title)}</h3>
                  <p className="mt-3 text-white/50 text-sm leading-relaxed flex-1">{t(s.desc)}</p>
                  <button data-testid={`service-learn-${i}`} onClick={() => setActive(s)}
                    className="group/btn mt-6 inline-flex items-center gap-2 text-white font-600 text-sm self-start">
                    {t("Learn More")} <ArrowRight className="h-4 w-4 group-hover/btn:translate-x-1 transition-transform" />
                  </button>
                </div>
              </div>
            </Reveal>
          ))}
        </div>
      </section>

      <section className="bg-ink-900 py-24 md:py-32">
        <div className="max-w-4xl mx-auto px-6 text-center">
          <Reveal><h2 className="font-serif text-gradient text-3xl md:text-5xl leading-tight" style={{ fontWeight: 500 }}>{t("Not sure which program fits?")}</h2></Reveal>
          <Reveal delay={0.15}><p className="mt-5 text-white/55 text-lg">{t("Try our AI Quote Generator for an instant, tailored estimate — or book a free walkthrough.")}</p></Reveal>
          <Reveal delay={0.25}>
            <div className="mt-8 flex flex-col sm:flex-row gap-4 justify-center">
              <Link to="/quote" className="btn-pill group inline-flex items-center justify-center gap-2 bg-white text-ink-950 pl-8 pr-6 py-4 text-sm font-600 hover:shadow-xl hover:shadow-white/20">
                {t("Get an Instant Quote")} <ArrowRight className="h-4 w-4 group-hover:translate-x-1 transition-transform" />
              </Link>
              <Link to="/contact" className="btn-pill glass text-white px-8 py-4 text-sm font-600 hover:bg-white/10">
                {t("Book a Walkthrough")}
              </Link>
            </div>
          </Reveal>
        </div>
      </section>

      <Dialog open={!!active} onOpenChange={(o) => !o && setActive(null)}>
        <DialogContent className="glass-strong border-white/10 rounded-3xl max-w-lg text-white p-0 overflow-hidden">
          {active && (
            <>
              <div className="relative aspect-[16/9]">
                <img src={IMAGES[active.img]} alt={active.title} className="absolute inset-0 w-full h-full object-cover opacity-60" />
                <div className="absolute inset-0 bg-gradient-to-t from-ink-900 to-transparent" />
                <div className="absolute bottom-4 left-6 h-12 w-12 rounded-2xl glass flex items-center justify-center">
                  <active.icon className="h-6 w-6 text-steel-light" />
                </div>
              </div>
              <div className="p-7">
                <DialogHeader>
                  <DialogTitle className="font-serif text-2xl text-white" style={{ fontWeight: 500 }}>{t(active.title)}</DialogTitle>
                </DialogHeader>
                <p className="mt-3 text-white/60 leading-relaxed">{t(active.desc)}</p>
                <div className="mt-6 grid grid-cols-2 gap-3">
                  {active.includes.map((it) => (
                    <div key={it} className="flex items-center gap-2 text-sm text-white/75">
                      <Check className="h-4 w-4 text-steel-light shrink-0" /> {t(it)}
                    </div>
                  ))}
                </div>
                <Link to="/quote" className="btn-pill mt-7 inline-flex items-center gap-2 bg-white text-ink-950 px-6 py-3 text-sm font-600 w-full justify-center">
                  {t("Request a Quote for")} {t(active.title)}
                </Link>
              </div>
            </>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
